import React from 'react';
import { motion } from 'framer-motion';
import { AlertTriangleIcon, CheckIcon, StarIcon } from 'lucide-react';
import { useCompletion } from '../contexts/CompletionContext';
import type { Slot } from '../types/content';
import { pillarStyles } from '../utils/stats';
import { countdownLabel, getStatus, statusStyles } from '../utils/status';
interface SlotCardProps {
  slot: Slot;
  index: number;
}
export function SlotCard({
  slot,
  index
}: SlotCardProps) {
  const {
    isDone,
    toggle
  } = useCompletion();
  const id = slot.id ?? `${slot.date}-${slot.topic}`;
  const done = isDone(id);
  const status = getStatus(slot.date, done);
  const s = statusStyles[status];
  return <motion.article layout initial={{
    opacity: 0,
    y: 8
  }} animate={{
    opacity: 1,
    y: 0
  }} transition={{
    duration: 0.28,
    delay: Math.min(index * 0.02, 0.24),
    ease: 'easeOut'
  }} className={`relative overflow-hidden rounded-xl border pl-4 pr-3 py-3 transition-colors duration-300 ${s.border} ${s.surface}`} data-id="element-92" data-mp="slkvcolgg1r9">
      <span className={`absolute inset-y-0 left-0 w-[3px] ${s.rail}`} aria-hidden="true" data-id="element-93" data-mp="1q0u30jnan74n" />

      <div className="flex items-start justify-between gap-2" data-id="element-94" data-mp="1u0w3wql7fwiv">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-ink-700 bg-ink-850 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-mute" data-id="element-95" data-mp="1u3ib2p46oa0k">
          <span className={`h-1.5 w-1.5 rounded-full ${pillarStyles[slot.pillar].dot}`} aria-hidden="true" data-id="element-96" data-mp="23z2330fmmok1" />
          {slot.pillar}
        </span>

        <div className="flex items-center gap-1.5" data-id="element-97" data-mp="1bamsqr7fwuf1">
          {slot.hero && !done && <StarIcon className="h-3.5 w-3.5 text-campaign" aria-label="Hero moment" data-id="element-98" data-mp="1ga94sxbj84m9" />}
          {slot.blocked && !done && <AlertTriangleIcon className="h-3.5 w-3.5 text-alert" aria-label="Blocked" data-id="element-99" data-mp="1l0pj0jn6fu9o" />}
          <button type="button" role="checkbox" aria-checked={done} onClick={() => toggle(id)} aria-label={done ? `Mark “${slot.topic}” as not done` : `Mark “${slot.topic}” as done`} className={`flex h-5 w-5 items-center justify-center rounded-md border transition-colors ${done ? 'border-ink-500 bg-ink-600 text-chalk' : 'border-ink-600 text-transparent hover:border-ink-500 hover:text-faint'}`} data-id="element-100" data-mp="nlleuofu8r2d">
            <CheckIcon className="h-3 w-3" aria-hidden="true" data-id="element-101" data-mp="payzdcc071kn" />
          </button>
        </div>
      </div>

      <p className={`mt-2 text-[13px] leading-snug transition-colors ${done ? 'text-faint line-through decoration-ink-500' : 'text-chalk'}`} data-id="element-102" data-mp="1kl297ekacj7">
        {slot.topic}
      </p>

      <dl className="mt-3 space-y-1 border-t border-ink-700/70 pt-2 font-mono text-[10px] text-faint" data-id="element-103" data-mp="3tr9ukualpzyc">
        <div className="flex items-center justify-between gap-2" data-id="element-104" data-mp="6k7fhgue3ss0">
          <dt className="sr-only" data-id="element-105" data-mp="6iwcvkcokr2h">Channels</dt>
          <dd className={done ? 'text-faint' : 'text-mute'} data-id="element-106" data-mp="1my7gkv4z1ogu">{slot.channels}</dd>
          <dt className="sr-only" data-id="element-107" data-mp="1n0aavrd5q3rd">Status</dt>
          <dd className={`shrink-0 ${s.text}`} data-id="element-108" data-mp="1hybamqcprmyn">{done ? 'Done' : countdownLabel(slot.date)}</dd>
        </div>
        <div className="flex justify-between gap-2" data-id="element-109" data-mp="2zw7ycvz8969j">
          <dt className="sr-only" data-id="element-110" data-mp="34w2k6l45b5nq">Product</dt>
          <dd data-id="element-111" data-mp="34wvl35gfb0xy">{slot.product}</dd>
          <dt className="sr-only" data-id="element-112" data-mp="34tpvzq7snz28">Format</dt>
          <dd className="text-right" data-id="element-113" data-mp="2qx31du3bdkzh">{slot.format}</dd>
        </div>
      </dl>
    </motion.article>;
}